import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useDateFormat } from '../contexts/DateFormatContext';
import { useOddsFormat } from '../contexts/OddsFormatContext';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  TrendingUp,
  Calendar,
  Target,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Edit,
  ArrowLeft,
  Search,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import Price from '../components/Price';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminTips = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { formatDate } = useDateFormat();
  const { convertOdds } = useOddsFormat();
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingTip, setEditingTip] = useState(null);
  const [editForm, setEditForm] = useState({
    status: '',
    analysis: ''
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchAllTips();
  }, [user, navigate]);

  const fetchAllTips = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/admin/tips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTips(response.data);
    } catch (error) {
      console.error('Error fetching tips:', error);
      toast.error('Failed to load tips');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTip = async (tipId) => {
    if (!window.confirm('Are you sure you want to delete this tip? This action cannot be undone and will affect platform integrity.')) {
      return;
    }

    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${API}/tips/${tipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tip deleted successfully');
      fetchAllTips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tip');
    }
  };

  const handleEditTip = (tip) => {
    setEditingTip(tip);
    setEditForm({
      status: tip.status,
      analysis: tip.analysis || ''
    });
  };

  const handleUpdateTip = async () => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(`${API}/admin/tips/${editingTip.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tip updated successfully');
      setEditingTip(null);
      fetchAllTips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tip');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'won': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'lost': return <XCircle className="w-5 h-5 text-red-400" />;
      case 'void': return <XCircle className="w-5 h-5 text-gray-400" />;
      default: return <Clock className="w-5 h-5 text-yellow-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'won': return 'bg-green-900/30 text-green-400 border-green-500/30';
      case 'lost': return 'bg-red-900/30 text-red-400 border-red-500/30';
      case 'void': return 'bg-gray-900/30 text-gray-400 border-gray-500/30';
      default: return 'bg-yellow-900/30 text-yellow-400 border-yellow-500/30';
    }
  };

  const filteredTips = tips.filter(tip => {
    const matchesSearch = tip.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tip.tipster_username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tip.matches?.some(m =>
        m.home_team?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.away_team?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    const matchesStatus = statusFilter === 'all' || tip.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading tips...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-blue-400" />
                Tips Management
              </h1>
            </div>
          </div>

          {/* Filters */}
          <Card className="glass-dark border-white/10 mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Label className="text-white mb-2">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search by service, tipster, or team..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
                <div className="w-full md:w-48">
                  <Label className="text-white mb-2">Status Filter</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="won">Won</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                      <SelectItem value="void">Void</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4 text-gray-400 text-sm">
                Total Tips: {filteredTips.length} / {tips.length}
              </div>
            </CardContent>
          </Card>

          {/* Tips List */}
          {filteredTips.length === 0 ? (
            <Card className="glass-dark border-white/10">
              <CardContent className="py-12 text-center">
                <TrendingUp className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No tips found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredTips.map((tip) => {
                const tipDate = new Date(tip.tip_date);
                const createdDate = new Date(tip.created_at);

                return (
                  <Card key={tip.id} className="glass-dark border-white/10 hover:border-blue-500/30 transition-all">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row justify-between gap-4">
                        {/* Left: Tip Info */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                {getStatusIcon(tip.status)}
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(tip.status)}`}>
                                  {tip.status.toUpperCase()}
                                </span>
                                {tip.is_parlay && (
                                  <span className="px-3 py-1 rounded-full text-xs bg-purple-900/30 text-purple-400 border border-purple-500/30">
                                    PARLAY ({tip.matches?.length} matches)
                                  </span>
                                )}
                                {tip.price === 0 && (
                                  <span className="px-3 py-1 rounded-full text-xs bg-green-900/30 text-green-400 border border-green-500/30">
                                    FREE
                                  </span>
                                )}
                                {tip.purchase_count > 0 && (
                                  <span className="px-3 py-1 rounded-full text-xs bg-blue-900/30 text-blue-400 border border-blue-500/30">
                                    {tip.purchase_count} Purchases
                                  </span>
                                )}
                              </div>
                              <h3 className="text-white text-lg font-semibold mb-1">
                                {tip.matches?.length === 1
                                  ? `${tip.matches[0].home_team} vs ${tip.matches[0].away_team}`
                                  : `${tip.matches?.length || 0} Match Parlay`}
                              </h3>
                              <p className="text-gray-400 text-sm">
                                Service: <span className="text-white">{tip.service_name || 'Unknown'}</span>
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                            <div>
                              <p className="text-gray-400 text-xs">Tip Date</p>
                              <p className="text-white flex items-center gap-1 text-sm">
                                <Calendar className="w-4 h-4" />
                                {formatDate(tip.tip_date)}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">Created</p>
                              <p className="text-white text-sm">
                                {formatDate(tip.created_at)}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">Total Odds</p>
                              <p className="text-white text-lg font-bold">{convertOdds(tip.total_odds)}</p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">Confidence</p>
                              <p className="text-white flex items-center gap-1">
                                <Target className="w-4 h-4" />
                                {tip.confidence_level}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-400 text-xs">Price</p>
                              <p className="text-blue-400 font-semibold flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                <Price amount={tip.price} />
                              </p>
                            </div>
                          </div>

                          {/* Matches */}
                          <div className="space-y-2">
                            {tip.matches?.map((match, idx) => (
                              <div key={idx} className="bg-white/5 rounded p-3">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <p className="text-white font-semibold text-sm">
                                      {match.home_team} vs {match.away_team}
                                    </p>
                                    <p className="text-gray-400 text-xs">{match.league}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-white font-bold">@ {convertOdds(match.odds)}</p>
                                    <p className="text-gray-400 text-xs">{match.market}</p>
                                  </div>
                                </div>
                                {match.prediction && (
                                  <p className="text-blue-400 text-xs mt-1">
                                    Prediction: {match.prediction}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Right: Actions */}
                        <div className="flex flex-col justify-between items-end gap-4 min-w-[180px]">
                          <div className="flex flex-col gap-2 w-full">
                            <Button
                              size="sm"
                              onClick={() => handleEditTip(tip)}
                              className="bg-blue-600 hover:bg-blue-700 w-full"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Tip
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleDeleteTip(tip.id)}
                              variant="destructive"
                              className="bg-red-600 hover:bg-red-700 w-full"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Tip
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingTip && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="glass-dark border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span>Edit Tip</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditingTip(null)}
                  className="text-white hover:bg-white/10"
                >
                  ✕
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-white mb-2">Status</Label>
                <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                    <SelectItem value="void">Void</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white mb-2">Analysis</Label>
                <Textarea
                  value={editForm.analysis}
                  onChange={(e) => setEditForm({ ...editForm, analysis: e.target.value })}
                  className="bg-white/5 border-white/10 text-white min-h-[120px]"
                  placeholder="Update tip analysis..."
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleUpdateTip}
                  className="bg-blue-600 hover:bg-blue-700 flex-1"
                >
                  Save Changes
                </Button>
                <Button
                  onClick={() => setEditingTip(null)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 flex-1"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
};

export default AdminTips;
